/**
 * German copy for the refusals `POST /legal/TSS/ReInitialize` can answer with (#613), plus the generic fallback
 * for a failed `GET /legal/TSS/GetCurrent`.
 *
 * **Never render `ApiError.description`.** It is English, written for `ErrorCatalog`/Postman/logs, and the SPA
 * has no i18n layer — see the frontend style guide, *"Surfacing an API error — branch on `code`, never render
 * `description`"*, and `_auth/two-factor/second-factor-errors.ts` for the pattern this file follows
 * (`readErrorCode`, a `Record<string, string>`, a fixed German fallback). Colocated with the legal module
 * rather than under `_shared/errors/` because this vocabulary has exactly one consumer
 * (`TechnicalSecuritySystemComponent`).
 *
 * The stable `ApiError.code` itself is safe to show alongside the mapped sentence — it is an identifier, not
 * language, and naming it on an admin/support-facing screen like this one is useful for triage. Only
 * `description` is off limits.
 */

import {TechnicalSecuritySystemModel} from "../_models/technical-security-system-model";
import {LegalTssReinitializeStep} from "../_models/_enums/legal-tss-reinitialize-step";
import {readErrorCode} from "../../../_auth/two-factor/second-factor-errors";

export {readErrorCode};

/** The `#613` wire codes, exactly as the monolith's `LegalTssErrorCodes` registers them. */
export const LegalTssErrorCodes = {
  /** A cash point (or one `PreparedForClosing`) is still open — nothing was changed. */
  ReinitializeBlockedCashPointOpen: "E_LEGAL_TSS_REINIT_BLOCKED_CASH_POINT_OPEN",
  /** The provider still reports the current TSE as active — nothing was changed. */
  ReinitializeBlockedProviderActive: "E_LEGAL_TSS_REINIT_BLOCKED_PROVIDER_ACTIVE",
  /** The shard is already on the new TSS id; calling `ReInitialize` again resumes at `Create`. */
  ReinitializeResumeAtCreate: "E_LEGAL_TSS_REINIT_RESUME_AT_CREATE",
  /** The new TSS was created but not initialised; `details.tss` is it. Calling again resumes at `Initialize`. */
  ReinitializeResumeAtInitialize: "E_LEGAL_TSS_REINIT_RESUME_AT_INITIALIZE"
} as const;

/** The `RESUME_AT_*` codes — the two failures that offer a "Fortsetzen" action instead of just a message. */
const RESUMABLE_CODES: ReadonlySet<string> = new Set([
  LegalTssErrorCodes.ReinitializeResumeAtCreate,
  LegalTssErrorCodes.ReinitializeResumeAtInitialize
]);

/** `error.details`, as `ReInitialize` sends it on a `RESUME_AT_*`/`BLOCKED_*` refusal. */
export interface LegalTssReinitializeErrorDetails {
  step?: LegalTssReinitializeStep;
  tss?: TechnicalSecuritySystemModel;
  providerError?: string;
}

/** Reads `error.error.details`, defensively — present only on the `ReInitialize` step-shaped refusals. */
export function readErrorDetails(err: unknown): LegalTssReinitializeErrorDetails | null {
  const details = (err as { error?: { details?: unknown } } | null)?.error?.details;
  return details && typeof details === "object" ? details as LegalTssReinitializeErrorDetails : null;
}

/** Whether `err` is a `RESUME_AT_*` refusal — offer "Fortsetzen" rather than just showing the message. */
export function isResumableReinitializeError(err: unknown): boolean {
  const code = readErrorCode(err);
  return code !== null && RESUMABLE_CODES.has(code);
}

const REINITIALIZE_MESSAGES: Readonly<Record<string, string>> = {
  [LegalTssErrorCodes.ReinitializeBlockedCashPointOpen]:
    "Es sind noch Kassenpunkte geöffnet. Schließen Sie alle Kassenpunkte dieses Standorts, bevor Sie die " +
    "technische Sicherheitseinrichtung neu initialisieren.",
  [LegalTssErrorCodes.ReinitializeBlockedProviderActive]:
    "Der Anbieter meldet die aktuelle technische Sicherheitseinrichtung weiterhin als aktiv. Eine " +
    "Neuinitialisierung ist erst möglich, wenn der Anbieter sie als deaktiviert, defekt, entzogen oder " +
    "gelöscht meldet.",
  [LegalTssErrorCodes.ReinitializeResumeAtCreate]:
    "Die vorherige technische Sicherheitseinrichtung wurde bereits zurückgezogen, die neue wurde aber noch " +
    "nicht angelegt. Setzen Sie den Vorgang fort, um sie jetzt zu erstellen.",
  [LegalTssErrorCodes.ReinitializeResumeAtInitialize]:
    "Die neue technische Sicherheitseinrichtung wurde bereits angelegt, aber noch nicht initialisiert. " +
    "Setzen Sie den Vorgang fort, um die Initialisierung abzuschließen."
};

/** The German sentence for a failed `ReInitialize` call. Unmapped codes (and a bare status) get the fallback. */
export function describeReinitializeError(err: unknown): string {
  const code = readErrorCode(err);
  return (code && REINITIALIZE_MESSAGES[code])
    || "Die technische Sicherheitseinrichtung konnte nicht neu initialisiert werden.";
}

/**
 * The German sentence for a failed `GetCurrent` load. `GetCurrent` has no documented structured codes of its
 * own (Phase 6 contract) — the page still stays rendered with this text plus the raw `code` (never
 * `description`), so a code added to the backend later degrades to this fixed sentence rather than to English.
 */
export function describeLoadError(): string {
  return "Die technische Sicherheitseinrichtung konnte nicht geladen werden.";
}
